/**
 * A group member represents an existing site member that has joined a group.
 * You can add, remove, and query group members.
 * Learn more about [group members](https://support.wix.com/en/article/wix-groups-adding-new-members-to-your-group).
 */
export interface GroupMember {
  /**
   * Member ID. See the Members API for more details.
   * @readonly
   * @format GUID
   */
  memberId?: string;
  /** Group member role. */
  role?: GroupRole;
  /**
   * Date and time the group Member joined the group.
   * @readonly
   */
  joinedDate?: Date | null;
}

/**
 * A group member may have multiple roles in a single group.
 * Currently, only `MEMBER` and `ADMIN` roles are supported, but more roles may be available in the future.
 */
export interface GroupRole {
  /** Member's role. */
  value?: RoleWithLiterals;
}

export enum Role {
  /** Regular group member. */
  MEMBER = 'MEMBER',
  /** Group administrator. */
  ADMIN = 'ADMIN',
}

/** @enumType */
export type RoleWithLiterals = Role | 'MEMBER' | 'ADMIN';

export interface SocialGroupsEvent extends SocialGroupsEventPayloadOneOf {
  memberJoined?: MemberJoinedGroup;
  membersAdded?: MembersAddedToGroup;
  joinRequestsApproved?: JoinRequestsApproved;
  membersInvited?: MembersInvitedToGroup;
}

/** @oneof */
export interface SocialGroupsEventPayloadOneOf {
  memberJoined?: MemberJoinedGroup;
  membersAdded?: MembersAddedToGroup;
  joinRequestsApproved?: JoinRequestsApproved;
  membersInvited?: MembersInvitedToGroup;
}

export interface MemberJoinedGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberId?: string;
}

export interface MembersAddedToGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  whoAddedId?: string | null;
  /** @format GUID */
  siteMemberIds?: string[];
  /**
   * Used for Apes sticky experiment
   * @format GUID
   */
  operationId?: string;
}

export interface JoinRequestsApproved {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberIds?: string[];
  /**
   * Used for Apes sticky experiment
   * @format GUID
   */
  operationId?: string;
}

export interface MembersInvitedToGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberIds?: string[];
}

/** ID of the group to join. */
export interface JoinRequest {
  /** @format GUID */
  groupId?: string;
  /**
   * Answers to membership questions. A Join Request will fail, if the answer to a required question is omitted.
   * @maxSize 50
   */
  membershipQuestionAnswers?: MembershipQuestionAnswer[];
  autoInviteId?: string | null;
}

/** Answer to a membership question. */
export interface MembershipQuestionAnswer {
  /**
   * Question ID.
   * @format GUID
   */
  id?: string;
  /**
   * Answer text.
   * @maxLength 300
   */
  text?: string | null;
}

export interface JoinResponse {
  /** New member. */
  member?: GroupMember;
}

export interface MemberJoined {
  /**
   * Group ID that member has joined.
   * @format GUID
   */
  groupId?: string;
  /** Joined group member. */
  groupMember?: GroupMember;
}

export enum JoinBy {
  MANUALLY = 'MANUALLY',
  PERSONAL_INVITE_LINK = 'PERSONAL_INVITE_LINK',
  PUBLIC_INVITE_LINK = 'PUBLIC_INVITE_LINK',
}

/** @enumType */
export type JoinByWithLiterals =
  | JoinBy
  | 'MANUALLY'
  | 'PERSONAL_INVITE_LINK'
  | 'PUBLIC_INVITE_LINK';

export interface LeaveRequest {
  /**
   * ID of the Group to leave.
   * @format GUID
   */
  groupId?: string;
}

export interface LeaveResponse {}

export interface MemberLeft {
  /**
   * Group ID that member has left.
   * @format GUID
   */
  groupId?: string;
  /** Group member that left. */
  groupMember?: GroupMember;
}

export interface AddGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Member IDs. See the Members API for details.
   * @format GUID
   * @maxSize 100
   */
  memberIds?: string[];
}

export interface AddGroupMembersResponse {
  /** New members. */
  members?: GroupMember[];
}

export interface MemberAdded {
  /**
   * Group ID that member was added to.
   * @format GUID
   */
  groupId?: string;
  /** Added group member. */
  groupMember?: GroupMember;
}

export interface RemoveGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Member IDs. See the Members API for details.
   * @format GUID
   * @maxSize 100
   */
  memberIds?: string[];
}

export interface RemoveGroupMembersResponse {}

export interface MemberRemoved {
  /**
   * Group ID that member was removed from.
   * @format GUID
   */
  groupId?: string;
  /** Removed group member. */
  groupMember?: GroupMember;
}

export interface ListGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListGroupMembersResponse {
  /** Retrieved members. */
  members?: GroupMember[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface QueryGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  query?: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryGroupMembersResponse {
  /** Group members. */
  members?: GroupMember[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface QueryNonGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId?: string;
  query?: Query;
}

export interface QueryNonGroupMembersResponse {
  /** Retrieved members. */
  members?: GroupMember[];
}

export interface ListMembershipsRequest {
  /**
   * Member ID. See the Members API for details.
   * @format GUID
   */
  memberId: string | null;
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListMembershipsResponse {
  /** Site member's memberships. */
  memberships?: Membership[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface Membership {
  /**
   * Group ID.
   * @readonly
   * @format GUID
   */
  groupId?: string;
  /** Membership status with group. */
  status?: MembershipStatusWithLiterals;
  /** Group member role. When membership status is not `JOINED`, this is empty. */
  role?: GroupRole;
}

export enum MembershipStatus {
  /** Undefined membership status. */
  UNKNOWN_STATUS = 'UNKNOWN_STATUS',
  /** Site member is a group member. */
  JOINED = 'JOINED',
  /** Site member has submitted a request to join this group. The request is still `PENDING`. */
  PENDING = 'PENDING',
}

/** @enumType */
export type MembershipStatusWithLiterals =
  | MembershipStatus
  | 'UNKNOWN_STATUS'
  | 'JOINED'
  | 'PENDING';

export interface QueryMembershipsRequest {
  /**
   * Member ID. See the Members API for details.
   * @format GUID
   */
  memberId: string | null;
  query?: Query;
}

export interface QueryMembershipsResponse {
  /** Site member's memberships. */
  memberships?: Membership[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface QueryMyMembershipRequest {
  query?: Query;
}

export interface QueryMyMembershipResponse {
  /** Site member's memberships. */
  memberships?: Membership[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
